import { useState, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import Modal from '../Modal'
import InputField from '../InputField'
import Select from '../Select'
import {
    PayrollFileFormat,
    PayrollSystemImportFileFormat,
    CalendarMonthPayoutBasisReportGenerationType,
    PayrollFileEmployeeIdentifierType,
    AdminFlowType,
    PayrollSystem,
    PayrollSystemConfigurationModel,
    EmployeeBankAccountRegisterType
} from '../../redux/types'
import S1 from '../typography/S1'
import Switch from '../Switch'

interface Props {
    initialValue?: PayrollSystemConfigurationModel
    onSaveClick?: (updateModel: PayrollSystemConfigurationModel) => void
    onCancelClick?: () => void
    visible: boolean
}

const EditPayrollSystemConfigurationModal = ({
    initialValue,
    onSaveClick,
    onCancelClick,
    visible
}: Props) => {
    const styles = useStyles()
    const [updateModel, setUpdateModel] = useState<PayrollSystemConfigurationModel>()

    useEffect(() => {
        if (initialValue) {
            setUpdateModel({...initialValue})
        }
    }, [initialValue])

    const handleOnSave = () => {
        if (updateModel) {
            onSaveClick && onSaveClick(updateModel)
        }
    }

    const handleOnClose = () => {
        onCancelClick && onCancelClick()
    }

    const handleOnChange = (propName: string, value: any) => {
        if (updateModel) {
            setUpdateModel({...updateModel, [propName]: value === '' ? null : value})
        }
    }

    const getPayrollFileFormatOptions = () => {
        return Object.keys(PayrollFileFormat).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    const getPayrollSystemOptions = () => {
        return Object.keys(PayrollSystem).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    const getPayrollSystemImportFileFormatOptions = () => {
        return Object.keys(PayrollSystemImportFileFormat).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    const getPayrollFileEmployeeIdentifierTypeOptions = () => {
        return Object.keys(PayrollFileEmployeeIdentifierType).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    const getCalendarMonthPayoutBasisReportGenerationTypeOptions = () => {
        return Object.keys(CalendarMonthPayoutBasisReportGenerationType).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    const getAdminFlowTypeOptions = () => {
        return Object.keys(AdminFlowType).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    const getEmployeeBankAccountRegisterTypeOptions = () => {
        return Object.keys(EmployeeBankAccountRegisterType).filter(v => isNaN(Number(v)))
            .map((value) => { return { name: value, value: value }
        })
    }

    return (
        <Modal
            title='Edit payroll system config'
            show={visible}
            okButtonTitle='Save'
            cancelButtonTitle='Cancel'
            onHide={() => handleOnClose()}
            onOkClick={() => handleOnSave()}
            onCancelClick={() => handleOnClose()}
        >
            <S1 className={styles.sectionTitle}>General</S1>
            <Select
                id='adminFlowType'
                key='adminFlowType'
                label='Admin flow'
                options={getAdminFlowTypeOptions()}
                selectedValue={updateModel?.adminFlowType.toString()}
                onSelect={(value) => handleOnChange('adminFlowType', value)}
            />
            <Select
                id='employeeBankAccountRegisterType'
                key='employeeBankAccountRegisterType'
                label='Employee bank account register'
                options={getEmployeeBankAccountRegisterTypeOptions()}
                selectedValue={updateModel?.employeeBankAccountRegisterType.toString()}
                onSelect={(value) => handleOnChange('employeeBankAccountRegisterType', value)}
            />
            <Select
                id='payrollSystem'
                key='payrollSystem'
                label='Payroll system'
                options={getPayrollSystemOptions()}
                selectedValue={updateModel?.payrollSystem.toString()}
                onSelect={(value) => handleOnChange('payrollSystem', value)}
            />
            <Select
                id='payrollFileFormat'
                key='payrollFileFormat'
                label='Payroll file format'
                options={getPayrollFileFormatOptions()}
                selectedValue={updateModel?.payrollFileFormat.toString()}
                onSelect={(value) => handleOnChange('payrollFileFormat', value)}
            />
            <Switch
                id='enablePayrollFileAdjustment'
                key='enablePayrollFileAdjustment'
                checked={updateModel?.enablePayrollFileAdjustment ?? false}
                label='Enable payroll file adjustment'
                onChange={() => handleOnChange('enablePayrollFileAdjustment', !(updateModel?.enablePayrollFileAdjustment ?? false))}
            />
            <Select
                id='payrollFileEmployeeIdentifierType'
                key='payrollFileEmployeeIdentifierType'
                label='Payroll file employee identification'
                options={getPayrollFileEmployeeIdentifierTypeOptions()}
                selectedValue={updateModel?.payrollFileEmployeeIdentifierType.toString()}
                onSelect={(value) => handleOnChange('payrollFileEmployeeIdentifierType', value)}
            />
            <Select
                id='payrollSystemImportFileFormat'
                key='payrollSystemImportFileFormat'
                label='Payroll system import file format'
                options={getPayrollSystemImportFileFormatOptions()}
                selectedValue={updateModel?.payrollSystemImportFileFormat.toString()}
                onSelect={(value) => handleOnChange('payrollSystemImportFileFormat', value)}
            />
            <Switch
                id='enablePayrollSystemEmployeeDeactivationImportFile'
                key='enablePayrollSystemEmployeeDeactivationImportFile'
                checked={updateModel?.enableEmployeeDeactivationImportFile ?? false}
                label='Enable payroll system employee deactivation import file'
                onChange={() => handleOnChange('enableEmployeeDeactivationImportFile', !(updateModel?.enableEmployeeDeactivationImportFile ?? false))}
            />
            <Switch
                id='enablePayrollSystemBankAccountImportFile'
                key='enablePayrollSystemBankAccountImportFile'
                checked={updateModel?.enableBankAccountImportFile ?? false}
                label='Enable payroll system bank account import file'
                onChange={() => handleOnChange('enableBankAccountImportFile', !(updateModel?.enableBankAccountImportFile ?? false))}
            />
            <InputField
              id={'payrollSystemBankAccountImportFileCustomField'}
              value={updateModel?.bankAccountImportFileCustomField ?? ''}
              onChange={(event) => handleOnChange('bankAccountImportFileCustomField', event.target.value)}
              label='Custom field for bank account import'
              placeholder=''
            />
            <Switch
                id='disablePaydayDeductionFileGeneration'
                key='disablePaydayDeductionFileGeneration'
                checked={updateModel?.disableDeductionFileGeneration ?? false}
                label='Disable payday deduction file generation'
                onChange={() => handleOnChange('disableDeductionFileGeneration', !(updateModel?.disableDeductionFileGeneration ?? false))}
            />
            <Select
                id='calendarMonthPayoutBasisReportGenerationType'
                key='calendarMonthPayoutBasisReportGenerationType'
                label='Calendar month payout basis report generation'
                options={getCalendarMonthPayoutBasisReportGenerationTypeOptions()}
                selectedValue={updateModel?.calendarMonthPayoutBasisReportGenerationType.toString()}
                onSelect={(value) => handleOnChange('calendarMonthPayoutBasisReportGenerationType', value)}
            />
            <InputField
                id='calenderMonthPayoutBasisReportGenerationDayOfMonth'
                numeric={true}
                value={updateModel?.calenderMonthPayoutBasisReportGenerationDayOfMonth?.toString() ?? ''}
                onChange={(event) => handleOnChange('calenderMonthPayoutBasisReportGenerationDayOfMonth', event.target.value)}
                label='Day of month for early calendar month payout basis report generation'
                placeholder=''
            />
            <InputField
                id='automaticPayrollCreationDaysBeforePayday'
                numeric={true}
                value={updateModel?.automaticPayrollCreationDaysBeforePayday?.toString() ?? ''}
                onChange={(event) => handleOnChange('automaticPayrollCreationDaysBeforePayday', event.target.value)}
                label='Automatic payroll creation days before payday'
                placeholder=''
            />
            <InputField
                id='payrollCreationDisabledUntilDaysBeforePayday'
                numeric={true}
                value={updateModel?.payrollCreationDisabledUntilDaysBeforePayday?.toString() ?? ''}
                onChange={(event) => handleOnChange('payrollCreationDisabledUntilDaysBeforePayday', event.target.value)}
                label='Disable payroll creation until days before payday'
                placeholder=''
            />
            <Switch
              id='payrollFileExecutionDateOneDayBeforePayment'
              key='payrollFileExecutionDateOneDayBeforePayment'
              checked={updateModel?.payrollFileExecutionDateOneDayBeforePayment ?? false}
              label='Payroll file execution date one day before payment'
              onChange={() => handleOnChange('payrollFileExecutionDateOneDayBeforePayment', !(updateModel?.payrollFileExecutionDateOneDayBeforePayment ?? false))}
            />
            <Switch
              id='manualPayrollFileRowCreationEnabled'
              key='manualPayrollFileRowCreationEnabled'
              checked={updateModel?.manualPayrollFileRowCreationEnabled ?? false}
              label='Manual payroll file row creation enabled'
              onChange={() => handleOnChange('manualPayrollFileRowCreationEnabled', !(updateModel?.manualPayrollFileRowCreationEnabled ?? false))}
            />
            <Switch
              id='payrollFileRowDeletionEnabled'
              key='payrollFileRowDeletionEnabled'
              checked={updateModel?.payrollFileRowDeletionEnabled ?? false}
              label='Payroll file row deletion enabled'
              onChange={() => handleOnChange('payrollFileRowDeletionEnabled', !(updateModel?.payrollFileRowDeletionEnabled ?? false))}
            />
            <InputField
              id={'payrollFileHandlerConfiguration'}
              value={updateModel?.payrollFileHandlerConfiguration ?? ''}
              onChange={(event) => handleOnChange('payrollFileHandlerConfiguration', event.target.value)}
              label='Payroll file handler config'
              placeholder=''
            />
            <Switch
              id='useParallelEmploymentContracts'
              key='useParallelEmploymentContracts'
              checked={updateModel?.useParallelEmploymentContracts ?? false}
              label='Use parallel employment contracts'
              onChange={() => handleOnChange('useParallelEmploymentContracts', !(updateModel?.useParallelEmploymentContracts ?? false))}
            />
        </Modal>
    )
}

export default EditPayrollSystemConfigurationModal

const useStyles = createUseStyles({
    container: {
        alignItems: 'center',
        textAlign: 'center',
        padding: '12px 20px',
        margin: '10px 0',
    },
    sectionTitle: {
        marginBottom: 15
    }
})
